import type {
  OpticalMeasureSHSpreadMetric,
  SHCaptureAggregateData,
} from '../../src/ExpoOpticalMeasure.types'

/**
 * Stability of a single capture: how far apart the accepted samples of one
 * session landed, against the gate native was configured with. Which statistic
 * and which gate depends on `sh.capture.spreadMetric` — `mad` (default,
 * n-independent) or `range` (1.1 behavior).
 *
 * Native already computes this verdict as `aggregate.highSpread`, which is the
 * OR of the two sides. This is the per-side form, because the review surfaces
 * it per result card — and because until now nothing read the flag at all: we
 * measured "this capture was unstable" and threw the finding away.
 *
 * Advisory, never a capture reject, for the same reason as the vbox range rule:
 * a capture with no timeout plus a hard reject strands the user in a scan that
 * cannot succeed. Unlike that rule, though, a repeat *can* fix this one, so the
 * message should ask for another capture rather than a different frame.
 */

export type ShSampleSpreadCheck = {
  spreadLeftMm: number
  spreadRightMm: number
  /** The gate the spreads were judged against, in `metric`'s units. */
  maxMm: number
  /** Which statistic `spreadLeftMm` / `spreadRightMm` are. */
  metric: OpticalMeasureSHSpreadMetric
  isLeftOutside: boolean
  isRightOutside: boolean
}

export type ShSampleSpreadConfig = {
  /** Overrides the gate native published alongside the aggregate. */
  maxMm?: number
}

/** The side that wobbled most — what a message should quote. */
export const shSampleSpreadMaxMm = (check: ShSampleSpreadCheck): number =>
  Math.max(check.spreadLeftMm, check.spreadRightMm)

/**
 * `null` when there is no aggregate yet, when the gate is switched off (native
 * omits the gate at 0), when a capture produced too few samples to have a
 * spread, or when both sides are inside it.
 *
 * Independent of `multiFrameMedianEnabled`: on best-frame the reported value is
 * one detection rather than the median, but a capture whose samples disagreed
 * is just as poor a basis for it.
 */
export const checkShSampleSpread = (
  aggregate: SHCaptureAggregateData | undefined,
  config: ShSampleSpreadConfig = {}
): ShSampleSpreadCheck | null => {
  if (!aggregate) return null

  const maxMm =
    config.maxMm ?? aggregate.stabilityGateMm ?? aggregate.maxSampleSpreadMm
  if (maxMm == null || !Number.isFinite(maxMm) || maxMm <= 0) return null

  // Follow whichever statistic native gated on, so the numbers in the message
  // are the numbers the verdict was taken on. Older payloads carry no metric
  // and only ever published the range.
  const metric: OpticalMeasureSHSpreadMetric = aggregate.spreadMetric ?? 'range'
  const spreadLeftMm =
    metric === 'mad' ? aggregate.dispersionLeft : aggregate.spreadLeft
  const spreadRightMm =
    metric === 'mad' ? aggregate.dispersionRight : aggregate.spreadRight
  if (spreadLeftMm == null || spreadRightMm == null) return null
  if (!Number.isFinite(spreadLeftMm) || !Number.isFinite(spreadRightMm)) {
    return null
  }

  const isLeftOutside = spreadLeftMm > maxMm
  const isRightOutside = spreadRightMm > maxMm
  if (!isLeftOutside && !isRightOutside) return null

  return {
    spreadLeftMm,
    spreadRightMm,
    maxMm,
    metric,
    isLeftOutside,
    isRightOutside,
  }
}
